import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import MarketCard from '@/components/MarketCard';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { TrendUp, Globe, Sparkle, Lightning, CheckCircle, Trophy, Fire } from '@phosphor-icons/react';
import { toast } from 'sonner';

const CATEGORIES = [
  { value: 'all', label: 'All Markets' },
  { value: 'economics', label: 'Economics' },
  { value: 'politics', label: 'Politics' },
  { value: 'technology', label: 'Technology' },
  { value: 'crypto', label: 'Crypto' },
  { value: 'sports', label: 'Sports' },
  { value: 'climate', label: 'Climate' },
  { value: 'entertainment', label: 'Entertainment' },
];

export default function HomePage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [markets, setMarkets] = useState([]);
  const [countries, setCountries] = useState({});
  const [selectedCountry, setSelectedCountry] = useState('Global');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showResolved, setShowResolved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [challenge, setChallenge] = useState(null);
  const [challengePredicting, setChallengePredicting] = useState(false);

  const fetchMarkets = useCallback(async () => {
    try {
      const params = {};
      if (selectedCountry !== 'Global') params.country = selectedCountry;
      if (selectedCategory !== 'all') params.category = selectedCategory;
      if (!showResolved) params.status = 'active';
      else params.status = '';
      const { data } = await api.get('/markets', { params });
      setMarkets(data);
    } catch (err) {
      console.error('Failed to fetch markets:', err);
    } finally {
      setLoading(false);
    }
  }, [api, selectedCountry, selectedCategory, showResolved]);

  const fetchCountries = useCallback(async () => {
    try {
      const { data } = await api.get('/countries');
      setCountries(data);
    } catch {}
  }, [api]);

  useEffect(() => { fetchCountries(); }, [fetchCountries]);
  useEffect(() => { fetchMarkets(); }, [fetchMarkets]);

  useEffect(() => {
    api.get('/daily-challenge').then(r => setChallenge(r.data)).catch(() => {});
  }, [api]);

  const handleChallengePredict = async (pos) => {
    setChallengePredicting(true);
    try {
      await api.post('/daily-challenge/predict', { position: pos });
      toast.success(`Prediction submitted: ${pos.toUpperCase()}!`);
      const { data } = await api.get('/daily-challenge');
      setChallenge(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Prediction failed');
    } finally { setChallengePredicting(false); }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const country = selectedCountry === 'Global' ? 'Nigeria' : selectedCountry;
      await api.post('/markets/generate', { country, category: selectedCategory === 'all' ? 'general' : selectedCategory });
      toast.success('New AI-generated markets created!');
      fetchMarkets();
    } catch (err) {
      toast.error('Failed to generate markets');
    } finally {
      setGenerating(false);
    }
  };

  const allCountries = Object.values(countries).flat();

  return (
    <div className="min-h-screen pt-24 px-4 md:px-8 pb-12" data-testid="home-page">
      <div className="max-w-7xl mx-auto">
        {/* Hero Section */}
        <div className="mb-10 animate-fade-up">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-2 h-2 rounded-full bg-[#00FFA3] pulse-dot" />
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#00FFA3]">Live Markets</span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-[var(--oh-text)] tracking-tight leading-none mb-3">
            Prediction<br />
            <span className="text-brand-gradient">Markets</span>
          </h1>
          <p className="text-[var(--oh-text-secondary)] text-base max-w-lg">
            AI-powered prediction questions on trending topics. Research, analyze, and trade on global events.
          </p>
        </div>

        {/* Daily Challenge */}
        {challenge?.challenge && (
          <div className="mb-8 rounded-xl p-5 md:p-6 border border-[rgba(245,158,11,0.3)] animate-fade-up relative overflow-hidden" style={{ backgroundColor: 'var(--oh-card)' }} data-testid="daily-challenge-card">
            <div className="absolute inset-0 bg-gradient-to-r from-[rgba(245,158,11,0.03)] to-transparent pointer-events-none" />
            <div className="relative z-10 flex flex-col md:flex-row md:items-center gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Fire size={18} className="text-[#F59E0B]" weight="fill" />
                  <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#F59E0B]">Daily Challenge</span>
                  {challenge.streak > 0 && (
                    <span className="text-xs font-mono text-[#F59E0B] bg-[rgba(245,158,11,0.1)] px-2 py-0.5 rounded-full border border-[rgba(245,158,11,0.2)]">
                      {challenge.streak} day streak
                    </span>
                  )}
                  <span className="text-xs font-mono text-[#00FFA3] ml-auto">+${challenge.challenge.bonus_amount} bonus</span>
                </div>
                <p className="font-heading text-base md:text-lg cursor-pointer hover:text-[#00FFA3] transition-colors" style={{ color: 'var(--oh-text)' }}
                  onClick={() => navigate(`/market/${challenge.challenge.market_id}`)}>
                  {challenge.challenge.market_question}
                </p>
                <p className="text-xs mt-1" style={{ color: 'var(--oh-text-muted)' }}>
                  {challenge.challenge.category} | {challenge.challenge.country} | Predict correctly to win bonus
                </p>
              </div>
              {challenge.user_prediction ? (
                <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[rgba(0,255,163,0.08)] border border-[rgba(0,255,163,0.2)]">
                  <CheckCircle size={18} className="text-[#00FFA3]" weight="fill" />
                  <span className="text-sm font-bold text-[#00FFA3]">Predicted: {challenge.user_prediction.position.toUpperCase()}</span>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Button onClick={() => handleChallengePredict('yes')} disabled={challengePredicting}
                    className="bg-[#00FFA3] text-black font-bold px-6 hover:shadow-[0_0_15px_rgba(0,255,163,0.3)] transition-all" data-testid="challenge-yes">
                    YES
                  </Button>
                  <Button onClick={() => handleChallengePredict('no')} disabled={challengePredicting}
                    className="bg-[#00B8FF] text-black font-bold px-6 hover:shadow-[0_0_15px_rgba(0,184,255,0.3)] transition-all" data-testid="challenge-no">
                    NO
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Filters Row */}
        <div className="flex flex-wrap items-center gap-3 mb-8 animate-fade-up" style={{ animationDelay: '0.1s' }}>
          <Select value={selectedCountry} onValueChange={setSelectedCountry}>
            <SelectTrigger className="w-48 bg-[var(--oh-card)] border-[var(--oh-border)] text-[var(--oh-text)] h-10 focus:ring-[#00FFA3]" data-testid="country-selector">
              <Globe size={16} className="mr-2 text-[#00B8FF]" />
              <SelectValue placeholder="Select Country" />
            </SelectTrigger>
            <SelectContent className="bg-[var(--oh-card)] border-[var(--oh-border)] text-[var(--oh-text)]">
              <SelectItem value="Global" className="focus:bg-[var(--oh-bg-tertiary)] focus:text-white">Global (All)</SelectItem>
              {allCountries.map(c => (
                <SelectItem key={c} value={c} className="focus:bg-[var(--oh-bg-tertiary)] focus:text-white">{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="flex gap-1.5 flex-wrap">
            {CATEGORIES.map(cat => (
              <button key={cat.value} onClick={() => setSelectedCategory(cat.value)}
                data-testid={`category-${cat.value}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 border ${
                  selectedCategory === cat.value
                    ? 'bg-[rgba(0,255,163,0.1)] text-[#00FFA3] border-[rgba(0,255,163,0.3)]'
                    : 'bg-[var(--oh-card)] text-[var(--oh-text-secondary)] border-[var(--oh-border)] hover:border-[var(--oh-border-hover)] hover:text-white'
                }`}>
                {cat.label}
              </button>
            ))}
          </div>

          <Button onClick={handleGenerate} disabled={generating} data-testid="generate-markets-button"
            className="ml-auto bg-[var(--oh-card)] border border-[var(--oh-border)] text-[var(--oh-text)] hover:border-[#00FFA3] hover:bg-[rgba(0,255,163,0.05)] transition-colors h-10">
            <Sparkle size={16} className="mr-2 text-[#00FFA3]" weight="fill" />
            {generating ? 'Generating...' : 'AI Generate'}
          </Button>

          <button onClick={() => setShowResolved(!showResolved)} data-testid="toggle-resolved"
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all border flex items-center gap-1.5 ${
              showResolved
                ? 'bg-[rgba(245,158,11,0.1)] text-[#F59E0B] border-[rgba(245,158,11,0.3)]'
                : 'bg-[var(--oh-card)] text-[var(--oh-text-muted)] border-[var(--oh-border)] hover:border-[var(--oh-border-hover)]'
            }`}>
            <CheckCircle size={14} weight="fill" /> Resolved
          </button>
        </div>

        {/* Markets Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {[1,2,3,4,5,6].map(i => (
              <div key={i} className="bg-[var(--oh-card)] border border-[var(--oh-border)] rounded-xl h-64 animate-pulse" />
            ))}
          </div>
        ) : markets.length === 0 ? (
          <div className="text-center py-20">
            <Lightning size={48} className="mx-auto mb-4 text-[var(--oh-text-muted)]" />
            <p className="text-[var(--oh-text-secondary)] text-lg mb-2">No markets found</p>
            <p className="text-[var(--oh-text-muted)] text-sm mb-6">Try a different filter or generate new AI-powered markets</p>
            <Button onClick={handleGenerate} disabled={generating}
              className="bg-gradient-to-r from-[#00FFA3] to-[#00B8FF] text-black font-bold hover:scale-[1.02] transition-transform">
              <Sparkle size={16} className="mr-2" weight="fill" />
              Generate Markets
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {markets.map((market, i) => (
              <div key={market.id} className="animate-fade-up" style={{ animationDelay: `${0.05 * i}s` }}>
                <MarketCard market={market} />
              </div>
            ))}
          </div>
        )}

        {/* Stats Footer */}
        {markets.length > 0 && (
          <div className="mt-12 flex items-center justify-center gap-8 text-[var(--oh-text-muted)] text-sm animate-fade-up" style={{ animationDelay: '0.3s' }}>
            <div className="flex items-center gap-2">
              <TrendUp size={16} className="text-[#00FFA3]" />
              <span className="font-mono">{markets.length}</span> Active Markets
            </div>
            <div className="flex items-center gap-2">
              <span className="font-mono">${markets.reduce((s, m) => s + (m.total_volume || 0), 0).toLocaleString()}</span> Total Volume
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
